// Client-side Tiller xlsx parser. Replaces the server-side parser for the
// chunked-upload flow — Workers Free can't fit a multi-megabyte xlsx parse
// in 10ms CPU. Browser has no CPU limit.
//
// Mirrors apps/api/src/services/tiller-parser/index.ts row shapes closely:
// the same fields, same Excel-serial-date conversion. We export the rows in
// the shapes that POST /uploads/:id/chunk expects.

import * as XLSX from 'xlsx';
import type {
  UploadChunkBalance,
  UploadChunkTransaction,
  UploadParseError,
} from '@frt/shared-types';

export interface ClientParsedFeed {
  balances: UploadChunkBalance[];
  transactions: UploadChunkTransaction[];
  /** Soft errors (missing optional column, malformed row coerced to a skip).
   *  Hard errors throw — caller catches and shows the user. */
  errors: UploadParseError[];
}

export class TillerParseClientError extends Error {
  code: string;
  sheet?: string;
  column?: string;
  constructor(code: string, message: string, opts: { sheet?: string; column?: string } = {}) {
    super(message);
    this.code = code;
    this.sheet = opts.sheet;
    this.column = opts.column;
  }
}

const REQUIRED_BALANCE_COLS = [
  'Date',
  'Account',
  'Account #',
  'Account ID',
  'Balance ID',
  'Institution',
  'Balance',
];
const REQUIRED_TRANSACTION_COLS = [
  'Date',
  'Description',
  'Amount',
  'Account ID',
  'Transaction ID',
];

/** Excel serial date → 'YYYY-MM-DD'. Excel epoch is 1899-12-30 (skips a leap
 *  year quirk). NaN-tolerant: invalid input throws so the caller can record
 *  a per-row error and skip. */
function serialToISODate(serial: number | string): string {
  const n = typeof serial === 'string' ? Number(serial) : serial;
  if (!Number.isFinite(n)) throw new TillerParseClientError('INVALID_DATE', `bad serial: ${serial}`);
  // Account for Excel's 1900 leap-year bug: dates ≥ Mar 1 1900 are 2 days
  // after Jan 0 1900 in our calendar. The standard offset (n - 25569) days
  // from Unix epoch handles this correctly for the dates Tiller emits.
  const ms = (n - 25569) * 86400 * 1000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) throw new TillerParseClientError('INVALID_DATE', `bad serial: ${serial}`);
  // Strip to YYYY-MM-DD (UTC).
  return d.toISOString().slice(0, 10);
}

function strOrNull(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  return String(v);
}

function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function requireColumns(
  row: Record<string, unknown>,
  cols: string[],
  sheet: string,
): void {
  for (const c of cols) {
    if (!(c in row)) {
      throw new TillerParseClientError(
        'MISSING_COLUMN',
        `sheet "${sheet}" missing required column: ${c}`,
        { sheet, column: c },
      );
    }
  }
}

export async function parseTillerWorkbookClient(
  buffer: ArrayBuffer,
): Promise<ClientParsedFeed> {
  // SheetJS handles xlsx (.xlsx and the 2003 xml format too).
  const wb = XLSX.read(buffer, { type: 'array', cellDates: false });

  const balanceSheetName = wb.SheetNames.find((s) => s.toLowerCase() === 'balance history');
  const transactionSheetName = wb.SheetNames.find((s) => s.toLowerCase() === 'transactions');
  if (!balanceSheetName) {
    throw new TillerParseClientError('MISSING_SHEET', 'workbook missing "Balance History" sheet', {
      sheet: 'Balance History',
    });
  }

  const errors: UploadParseError[] = [];
  const balances: UploadChunkBalance[] = [];
  const transactions: UploadChunkTransaction[] = [];

  // ---- Balance History ----
  const balanceRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
    wb.Sheets[balanceSheetName]!,
    { defval: null, raw: true },
  );
  if (balanceRows.length > 0) {
    requireColumns(balanceRows[0]!, REQUIRED_BALANCE_COLS, balanceSheetName);
  }
  for (let i = 0; i < balanceRows.length; i++) {
    const r = balanceRows[i]!;
    try {
      const tiller_account_id = strOrNull(r['Account ID']);
      const tiller_balance_id = strOrNull(r['Balance ID']);
      const balance = numOrNull(r['Balance']);
      if (!tiller_account_id || !tiller_balance_id || balance === null) {
        errors.push({
          code: 'ROW_INCOMPLETE',
          sheet: balanceSheetName,
          row: i + 2, // 1-based + header
          message: 'missing required field on balance row',
        });
        continue;
      }
      const observed_date = serialToISODate(r['Date'] as number | string);
      balances.push({
        tiller_account_id,
        tiller_balance_id,
        observed_date,
        balance,
        account_number_last4: strOrNull(r['Account #']),
        institution: strOrNull(r['Institution']),
        account_status: strOrNull(r['Account Status']),
        source: strOrNull(r['Source']),
        tiller_account_reference: strOrNull(r['Account']),
      });
    } catch (e) {
      const m = (e as Error).message;
      errors.push({
        code: e instanceof TillerParseClientError ? e.code : 'PARSE_FAILED',
        sheet: balanceSheetName,
        row: i + 2,
        message: m,
      });
    }
  }

  // ---- Transactions (optional) ----
  if (transactionSheetName) {
    const transactionRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
      wb.Sheets[transactionSheetName]!,
      { defval: null, raw: true },
    );
    if (transactionRows.length > 0) {
      requireColumns(transactionRows[0]!, REQUIRED_TRANSACTION_COLS, transactionSheetName);
    }
    for (let i = 0; i < transactionRows.length; i++) {
      const r = transactionRows[i]!;
      try {
        const tiller_account_id = strOrNull(r['Account ID']);
        const tiller_transaction_id = strOrNull(r['Transaction ID']);
        const amount = numOrNull(r['Amount']);
        if (!tiller_account_id || !tiller_transaction_id || amount === null) {
          errors.push({
            code: 'ROW_INCOMPLETE',
            sheet: transactionSheetName,
            row: i + 2,
            message: 'missing required field on transaction row',
          });
          continue;
        }
        transactions.push({
          tiller_account_id,
          tiller_transaction_id,
          posted_date: serialToISODate(r['Date'] as number | string),
          amount,
          description: strOrNull(r['Description']),
          full_description: strOrNull(r['Full Description']),
          category: strOrNull(r['Category']),
          category_hint: strOrNull(r['Category Hint']),
          check_number: strOrNull(r['Check Number']),
          source: strOrNull(r['Source']),
        });
      } catch (e) {
        const m = (e as Error).message;
        errors.push({
          code: e instanceof TillerParseClientError ? e.code : 'PARSE_FAILED',
          sheet: transactionSheetName,
          row: i + 2,
          message: m,
        });
      }
    }
  }

  return { balances, transactions, errors };
}

// ---------------------------------------------------------------------------
// Sync-state filtering: keep only rows newer than what the server has.
// ---------------------------------------------------------------------------

export interface SyncStateMap {
  // tiller_account_id → latest balance observed_at (YYYY-MM-DD), null/undefined = never seen
  balanceLatest: Map<string, string | null>;
  // tiller_account_id → latest transaction posted_date
  transactionLatest: Map<string, string | null>;
}

export function buildSyncStateMap(accounts: Array<{
  tiller_account_id: string;
  latest_balance_observed_at: string | null;
  latest_transaction_posted_date: string | null;
}>): SyncStateMap {
  const balanceLatest = new Map<string, string | null>();
  const transactionLatest = new Map<string, string | null>();
  for (const a of accounts) {
    balanceLatest.set(a.tiller_account_id, a.latest_balance_observed_at);
    transactionLatest.set(a.tiller_account_id, a.latest_transaction_posted_date);
  }
  return { balanceLatest, transactionLatest };
}

/** Drop balance rows whose observed_date is ≤ what the server already has.
 *  Tiller account IDs the server doesn't know about are kept (they show up
 *  as "unmatched" in the resolutions modal — the user has to link them). */
export function filterBalancesBySyncState(
  balances: UploadChunkBalance[],
  state: SyncStateMap,
): UploadChunkBalance[] {
  return balances.filter((b) => {
    const latest = state.balanceLatest.get(b.tiller_account_id);
    if (latest === undefined) return true; // unknown account
    if (latest === null) return true; // known account, no balance yet
    return b.observed_date > latest;
  });
}

export function filterTransactionsBySyncState(
  transactions: UploadChunkTransaction[],
  state: SyncStateMap,
): UploadChunkTransaction[] {
  return transactions.filter((t) => {
    const latest = state.transactionLatest.get(t.tiller_account_id);
    if (latest === undefined) return true;
    if (latest === null) return true;
    return t.posted_date > latest;
  });
}

/** Collapse to latest-per-account so even on a first-ever upload we don't
 *  flood the worker with daily history. The server only ever uses the
 *  latest balance per account anyway. */
export function collapseLatestBalancePerAccount(
  balances: UploadChunkBalance[],
): UploadChunkBalance[] {
  const latest = new Map<string, UploadChunkBalance>();
  for (const b of balances) {
    const prior = latest.get(b.tiller_account_id);
    if (!prior || b.observed_date > prior.observed_date) latest.set(b.tiller_account_id, b);
  }
  return [...latest.values()];
}

/** Build the unmatched-Tiller hints map the FE sends with /finalize. */
export function buildUnmatchedHints(
  balances: UploadChunkBalance[],
): Record<
  string,
  {
    latest_balance: number | null;
    observed_at: string | null;
    institution: string | null;
    account_number_last4: string | null;
    tiller_account_reference: string | null;
  }
> {
  const out: Record<
    string,
    {
      latest_balance: number | null;
      observed_at: string | null;
      institution: string | null;
      account_number_last4: string | null;
      tiller_account_reference: string | null;
    }
  > = {};
  for (const b of balances) {
    const cur = out[b.tiller_account_id];
    if (!cur || (cur.observed_at && b.observed_date > cur.observed_at)) {
      out[b.tiller_account_id] = {
        latest_balance: b.balance,
        observed_at: b.observed_date,
        institution: b.institution ?? null,
        account_number_last4: b.account_number_last4 ?? null,
        tiller_account_reference: b.tiller_account_reference ?? null,
      };
    }
  }
  return out;
}
